import type { JobAnalysisRequest, JobAnalysisResponse } from '../types';

// Job Matcher API configuration
const JOB_MATCHER_API_URL = 'https://aijobapplication-production.up.railway.app';

// API endpoints
const ENDPOINTS = {
  ANALYZE: '/analyze',
  HEALTH: '/health',
  READY: '/ready',
  CV_INFO: '/cv-info',
} as const;

class JobMatcherAPIError extends Error {
  constructor(
    message: string,
    public status?: number,
    public details?: any
  ) {
    super(message);
    this.name = 'JobMatcherAPIError';
  }
}

export const JobMatcherService = {
  /**
   * Analyze how well a job matches your CV
   */
  analyzeJob: async (jobText: string): Promise<JobAnalysisResponse> => {
    if (!jobText.trim()) {
      throw new JobMatcherAPIError('Job description cannot be empty');
    }

    try {
      const response = await fetch(`${JOB_MATCHER_API_URL}${ENDPOINTS.ANALYZE}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ job_text: jobText } as JobAnalysisRequest),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new JobMatcherAPIError(
          `API request failed: ${response.status} ${response.statusText}`,
          response.status,
          errorData
        );
      }

      const result: JobAnalysisResponse = await response.json();
      return result;
    } catch (error) {
      if (error instanceof JobMatcherAPIError) {
        throw error;
      }
      throw new JobMatcherAPIError(
        `Failed to analyze job: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  },

  /**
   * Check if the API is healthy
   */
  checkHealth: async (): Promise<string> => {
    try {
      const response = await fetch(`${JOB_MATCHER_API_URL}${ENDPOINTS.HEALTH}`);
      if (!response.ok) {
        throw new JobMatcherAPIError(`Health check failed: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      throw new JobMatcherAPIError(
        `Health check failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  },

  /**
   * Check if the API is ready to serve requests
   */
  checkReady: async (): Promise<string> => {
    try {
      const response = await fetch(`${JOB_MATCHER_API_URL}${ENDPOINTS.READY}`);
      if (!response.ok) {
        throw new JobMatcherAPIError(`Readiness check failed: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      throw new JobMatcherAPIError(
        `Readiness check failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  },

  /**
   * Get information about the loaded CV
   */
  getCVInfo: async (): Promise<string> => {
    try {
      const response = await fetch(`${JOB_MATCHER_API_URL}${ENDPOINTS.CV_INFO}`);
      if (!response.ok) {
        throw new JobMatcherAPIError(`CV info request failed: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      throw new JobMatcherAPIError(
        `CV info request failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  },
};

export { JobMatcherAPIError };